/**
 * v10.3 — the order the review queue's content batches are approved in.
 *
 * Pure data plus one pure derivation (`deriveContentBatchQueue`). The batch ids
 * are the `impact.batch_id` values the content migrations and the CR26 rules
 * ingest write; two migrations file rows without a batch id (328 GovRAMP rule
 * set, 339 Class A clocks), so their entries match by change-id prefix and are
 * approved row by row.
 *
 * Prerequisite rule: an entry is ready when no pending rows remain in any of
 * the entries it names in `after`. Rows the writer deferred (impact.apply_blocked)
 * are still pending, so they keep a dependent entry waiting — on purpose.
 *
 * Sources for each `after` edge are the migration headers:
 *   326  members apply once their baseline and their 324 requirement are applied
 *   331a after the 800-53 catalog batches (324/325) and cmmc-test-library@v102-1
 *   331b after 337 and after 331a
 *   332  after 331a and 331b; 350 after 331a, 331b and 332
 *   511/512 (v10.6 High-set catalog) need nothing; 514 after 511/512 and 331a, 331b, 332, 350
 *   519/520 (GovRAMP Low / High) members after their baseline row and their 324 / 511 requirement
 *   523/524 (Appendix D cross-maps) after the CMMC library and catalog and every earlier test-map batch
 *   337  after the CR26 rules batch
 *   341  CR26 rules -> 337 -> 331b (then the shadow-mode sweep, an owner gate)
 *   343  rows wait for their 324 requirement
 *   346  owner decision 5 (2026-10-07): 30 days after 341 is approved on production
 *   503/504 (SOC 2 points of focus) after the 204 criteria; 506 (SOC 2 maps) after its 503/504 series
 *        and after 331a, 331b, 332, 350 and 514 (versions build on theirs; 514 first, so the two never fork
 *        one test: scripts/content/check-test-version-chains.mjs); 508 (soc2-readiness switch, owner
 *        gate) after every SOC 2 objective and map batch, and on production only after V106-1-8 ships;
 *        507 (SEC -> SOC 2 edges become references) 30 days after 508 is approved on production
 *   525-527 (ISO 42001 clauses / Annex A) need nothing; 532 (ISO maps) after them and the test
 *        library; 531b (iso-certification rules, owner gate) after the ISO content
 *   630 (V108-8-11 CI gate test + maps) after the objectives it maps: 800-53 CM and SA (325c / 325i), the
 *        High-set CM objectives (512c), the KSI objectives (337) and the SOC 2 CC8 points of focus (503)
 *   734 (v11.1 AI threat crosswalk edges) after 733 (the threat-id nodes the edges point at)
 *   v11.3 (V113-6-3), in this order: 840 promptfoo signals (control specs, approved one by one); 804g/805–807
 *        monitoring rule sets (system class → prescriptions → scan vocabulary → detector candidates); the
 *        Track 2 anomaly rules (810–814, filed when Track 2 lands); 830–832 monitoring obligations after the
 *        ISO 42001 clause / Annex A batches (the test names their objectives; its control spec after the run);
 *        833–834 monitoring crosswalk after the SOC 2 catalog (204) and the 800-53 CA / SI batches (324).
 *   v11.4 (V114-6-3): 880 brief templates after the v11.3 monitoring rule sets (coverage reasons the
 *        templates speak to come from those prescriptions). The 883/884 SLA / retry batch follows the
 *        briefs and is added by the held Track 6b PR with 864 (its families need the lookup rows).
 *   v11.7 (V117-4-3 / 4-4): 1026–1034 state AI-law control batches. accuracy_testing / vendor_due_diligence after 956
 *        (it creates them); prohibited_ai_practices / training_data_transparency after 969 (996 files an EU rule into
 *        each), and v11.6's pack 1.1.0 specs for those two after them.
 */
export function isShipHold(hold) {
    return 'until_shipped' in hold;
}
/** The ship hold on a batch id, if any (the reason), else null. */
export function shipHoldForBatch(batchId, order = CONTENT_BATCH_ORDER) {
    if (!batchId)
        return null;
    const hold = findContentBatchEntry(batchId, order)?.entry.hold;
    return hold && isShipHold(hold) ? hold.reason : null;
}
/** 800-53 families: the GovRAMP Core/Ready families first, then the rest. */
export const NIST_800_53_FAMILY_ORDER = [
    'AC', 'AU', 'CM', 'CP', 'IA', 'IR', 'MA', 'RA', 'SA', 'SC', 'SI',
    'AT', 'CA', 'MP', 'PE', 'PL', 'PS', 'SR',
];
const CORE_READY_FAMILIES = NIST_800_53_FAMILY_ORDER.slice(0, 11);
const FAMILY_NAMES = {
    AC: 'Access Control', AU: 'Audit and Accountability', CM: 'Configuration Management',
    CP: 'Contingency Planning', IA: 'Identification and Authentication', IR: 'Incident Response',
    MA: 'Maintenance', RA: 'Risk Assessment', SA: 'System and Services Acquisition',
    SC: 'System and Communications Protection', SI: 'System and Information Integrity',
    AT: 'Awareness and Training', CA: 'Assessment, Authorization, and Monitoring',
    MP: 'Media Protection', PE: 'Physical and Environmental Protection', PL: 'Planning',
    PS: 'Personnel Security', SR: 'Supply Chain Risk Management',
};
/** KSI themes in 331b. */
const KSI_THEMES = ['CED', 'CMT', 'CNA', 'IAM', 'INR', 'MLA', 'PIY', 'RPL', 'SCR', 'SVC'];
const KSI_THEME_NAMES = {
    CED: 'Cybersecurity Education', CMT: 'Change Management', CNA: 'Cloud Native Architecture',
    IAM: 'Identity and Access Management', INR: 'Incident Response', MLA: 'Monitoring, Logging, and Auditing',
    PIY: 'Policy and Inventory', RPL: 'Recovery Planning', SCR: 'Supply Chain Risk', SVC: 'Service Configuration',
};
const nistKey = (f) => `nist-800-53:${f}`;
const tm1Key = (f) => `govramp-test-maps-1:${f}`;
const tm2Key = (f) => `govramp-test-maps-2:${f}`;
const ksiTmKey = (t) => `ksi-test-maps:${t}`;
const ALL_NIST = NIST_800_53_FAMILY_ORDER.map(nistKey);
const ALL_TM1 = CORE_READY_FAMILIES.map(tm1Key);
const ALL_KSI_TM = KSI_THEMES.map(ksiTmKey);
const ALL_TM2 = NIST_800_53_FAMILY_ORDER.map(tm2Key);
const GOVRAMP_MEMBERS = NIST_800_53_FAMILY_ORDER.map((f) => `govramp-moderate:${f}`);
/** v10.6 V106-2-2: the 16 families of the High-set extension (511/512); V106-2-5 maps all but PE (provider-held). */
const HIGH_SET_FAMILIES = NIST_800_53_FAMILY_ORDER.filter((f) => !['AT', 'PL'].includes(f));
const HIGH_MAP_FAMILIES = HIGH_SET_FAMILIES.filter((f) => f !== 'PE');
const nistHighKey = (f) => `nist-800-53-high:${f}`;
const ALL_TM3 = ['CM', 'IA'].map((f) => `govramp-test-maps-3:${f}`);
const ALL_HIGH_TM = HIGH_MAP_FAMILIES.map((f) => `high-set-test-maps:${f}`);
/** V106-4-3: SP 800-171 families of the Appendix D cross-map batches (523 forward, 524 reverse). */
const APP_D_FAMILIES = ['AC', 'AT', 'AU', 'CM', 'IA', 'IR', 'MA', 'MP', 'PS', 'PE', 'RA', 'CA', 'SC', 'SI'];
const APP_D_DIRECTIONS = [
    { key: 'app-d-forward', batch: 'app-d-maps-cmmc-to-800-53', label: 'Appendix D maps · CMMC tests → 800-53', migration: '523' },
    { key: 'app-d-reverse', batch: 'app-d-maps-800-53-to-cmmc', label: 'Appendix D maps · 800-53 tests → CMMC', migration: '524' },
];
/** V106-3-2: GovRAMP Low (519) and High (520) baselines. */
const LOW_HIGH_BASELINES = [
    { key: 'govramp-low', label: 'GovRAMP Low', version: 'V1.05', migration: '519' },
    { key: 'govramp-high', label: 'GovRAMP High', version: 'V1.1', migration: '520' },
];
/**
 * v11.7 V117-4-3 / 4-4: one batch per state AI-law control (1026-1034), in the generator's law-count order
 * (scripts/content/state-law-control-batches.mjs). A batch files the control, its BL-55 duty term and its guide;
 * the spec 1.0.0 of a new control is approved on its own after the run.
 */
const STATE_LAW_CONTROLS = [
    ['impact_assessment', '1026'], ['ai_interaction_disclosure', '1026'], ['opt_out_right', '1026'],
    ['ai_use_policy', '1027'], ['regulator_ai_reporting', '1027'], ['companion_crisis_protocol', '1027'],
    ['minor_user_protections', '1028'], ['anti_discrimination_safeguards', '1028'],
    ['professional_impersonation_prohibition', '1028'],
    ['ur_clinician_determination', '1029'], ['use_case_inventory', '1029'], ['prohibited_ai_practices', '1029'],
    ['ai_therapy_restriction', '1030'], ['pricing_algorithm_safeguards', '1030'], ['human_oversight', '1030'],
    ['restricted_data_use_for_ai', '1031'], ['consumer_notice', '1031'], ['professional_review_of_ai_output', '1031'],
    ['informed_consent_for_ai_use', '1031'], ['ai_generated_output_disclaimer', '1031'],
    ['altered_media_ad_disclosure', '1031'], ['pre_use_notice', '1031'], ['record_retention', '1031'],
    ['regulator_audit_access', '1031'], ['accuracy_testing', '1031'],
    ['appeal_process', '1032'], ['candidate_notice', '1032'], ['independent_compliance_audit', '1032'],
    ['synthetic_content_marking', '1032'], ['access_right', '1032'], ['adverse_decision_disclosure', '1032'],
    ['algorithmic_pricing_disclosure', '1032'], ['critical_incident_reporting', '1032'],
    ['decision_explanation', '1032'], ['frontier_safety_framework_publication', '1032'],
    ['frontier_transparency_report', '1032'],
    ['human_escalation_path', '1033'], ['public_workforce_protection', '1033'], ['risk_management_system', '1033'],
    ['whistleblower_protections', '1033'], ['audit_logging', '1033'], ['authentic_content_provenance', '1033'],
    ['bias_evaluation', '1033'], ['chatbot_advertising_limits', '1033'], ['data_broker_registration_disclosure', '1033'],
    ['developer_documentation', '1033'],
    ['platform_provenance_handling', '1034'], ['recommender_ranking_controls', '1034'],
    ['restricted_vendor_screening', '1034'], ['vendor_due_diligence', '1034'], ['bias_audit', '1034'],
    ['deletion_on_request', '1034'], ['governance_body', '1034'], ['human_intervention', '1034'],
    ['minimum_risk_practices', '1034'], ['ncii_notice_and_takedown', '1034'], ['privacy_program_ai_scope', '1034'],
    ['publish_audit_summary', '1034'], ['responsible_official', '1034'], ['risk_assessment', '1034'],
    ['training_data_transparency', '1034'],
];
/** Controls 956 (case-controls-v116) creates: their state-law guide (and vendor_due_diligence's term and spec) need the control row. */
const STATE_LAW_AFTER_956 = new Set(['accuracy_testing', 'vendor_due_diligence']);
/** 996 files an EU AI Act rule into these two batches; its header orders it after 969 (eu-ai-act-rules-v116). */
const STATE_LAW_EU_RULES = new Set(['prohibited_ai_practices', 'training_data_transparency']);
const stateLawKey = (id) => `state-ai-law-control-${id}-v117`;
const STATE_LAW_IDS = new Set(STATE_LAW_CONTROLS.map(([id]) => id));
const stateLawEntry = (id, migration) => ({
    key: stateLawKey(id), label: `State AI-law control: ${id.replace(/_/g, ' ')}`,
    migration: STATE_LAW_EU_RULES.has(id) ? `${migration}, 996` : migration,
    match: { kind: 'batch', batch_id: `state-ai-law-control-${id}-v117@1` },
    after: [...(STATE_LAW_AFTER_956.has(id) ? ['case-controls-v116'] : []), ...(STATE_LAW_EU_RULES.has(id) ? ['eu-ai-act-rules-v116'] : [])],
});
/** v11.6 V116-5-2 / 5-3: the pack legal controls' batches (983-994 checks + guide, 995 checks only). */
const PACK_GUIDED = [
    ['ai_cybersecurity', '983'], ['conformity_assessment', '984'], ['copyright_policy', '985'], ['data_governance', '986'],
    ['deepfake_disclosure', '987'], ['deployer_duties', '988'], ['eu_database_registration', '989'], ['generative_abuse_safeguards', '990'],
    ['operator_training', '991'], ['post_market_monitoring_plan', '992'], ['quality_management_system', '993'], ['technical_documentation', '994'],
];
const PACK_CHECKS = [
    'accuracy_testing', 'adverse_decision_disclosure', 'ai_interaction_disclosure', 'audit_logging', 'bias_audit', 'candidate_notice',
    'consumer_notice', 'critical_incident_reporting', 'developer_documentation', 'human_oversight', 'impact_assessment', 'pre_use_notice',
    'prohibited_ai_practices', 'publish_audit_summary', 'record_retention', 'risk_management_system', 'synthetic_content_marking',
    'training_data_transparency',
];
/** 966's new EU controls: their 1.1.0 waits for the 1.0.0 in eu-ai-act-controls-v116 (approved later, it would supersede 1.1.0). */
const EU_966_CONTROLS = new Set(['accuracy_testing', 'ai_cybersecurity', 'copyright_policy', 'data_governance', 'deepfake_disclosure',
    'eu_database_registration', 'generative_abuse_safeguards', 'quality_management_system']);
const packEntry = (id, migration, guide) => ({
    key: `pack-control-${id}-v116`, label: `${guide ? 'Checks and guide' : 'Checks'}: ${id.replace(/_/g, ' ')}`, migration,
    match: { kind: 'batch', batch_id: `pack-control-${id}-v116@1` },
    // A 1.1.0 approved before the 1.0.0 it builds on would be superseded by it: 966's, or a new state-law control's (1029 / 1034).
    // 997's guide version builds on v11.7's guide for every state-law control: approved first, v11.7's would replace it.
    after: [...(EU_966_CONTROLS.has(id) ? ['eu-ai-act-controls-v116'] : []), ...(STATE_LAW_IDS.has(id) ? [stateLawKey(id)] : [])],
});
export const FEDRAMP_READINESS_CHANGE_ID = 'chg-m341-fedramp-readiness-v1';
export const SOC2_READINESS_CHANGE_ID = 'chg-m508-soc2-readiness-v1';
/** v10.6 V106-1-2 / 1-4: SOC 2 point-of-focus series (503 CC1..CC9, 504 A1 / C1 / PI1 / P) and their map batches (506). */
const SOC2_SERIES = ['CC1', 'CC2', 'CC3', 'CC4', 'CC5', 'CC6', 'CC7', 'CC8', 'CC9', 'A1', 'C1', 'PI1', 'P'];
const soc2PofKey = (x) => `soc2-pof:${x}`;
const soc2MapKey = (x) => `soc2-maps:${x}`;
/** v10.6 V106-5-2: ISO 42001 content batches (525-527). */
const ISO_PARTS = [
    { key: 'iso-42001:clauses', batch: 'iso-42001@2023:clauses', label: 'ISO 42001 clauses 4–10 and objectives', migration: '525, 526' },
    { key: 'iso-42001:annex-a', batch: 'iso-42001@2023:annex-a', label: 'ISO 42001 Annex A controls and objectives', migration: '525, 527' },
];
export const CONTENT_BATCH_ORDER = [
    {
        stage: 1, key: 'cr26-rules', label: 'FedRAMP CR26 rules',
        entries: [
            {
                key: 'fedramp-rules', label: 'FedRAMP CR26 rules', migration: 'Rules ingest', match: { kind: 'batch_prefix', prefix: 'fedramp-rules@' }, after: [],
                when_missing: {
                    hint: 'Approve “Turns on a research source” for the FedRAMP rules source; the ingest files this batch within the hour',
                    action: 'research_source',
                },
            },
        ],
    },
    {
        stage: 2, key: 'nist-800-53', label: 'NIST SP 800-53 Rev 5 catalog',
        entries: NIST_800_53_FAMILY_ORDER.map((f) => ({
            key: nistKey(f), label: `800-53 ${f} · ${FAMILY_NAMES[f]}`, migration: '324, 325',
            match: { kind: 'batch', batch_id: `nist-800-53-r5@5.2.0:${f}` }, after: [],
        })).concat(HIGH_SET_FAMILIES.map((f) => ({
            key: nistHighKey(f), label: `800-53 High set ${f} · ${FAMILY_NAMES[f]}`, migration: '511, 512',
            match: { kind: 'batch', batch_id: `nist-800-53-r5-high@5.2.0:${f}` }, after: [],
        }))),
    },
    {
        stage: 3, key: 'govramp-baseline', label: 'GovRAMP Moderate baseline',
        entries: [
            { key: 'govramp-moderate', label: 'GovRAMP Moderate baseline', migration: '326', match: { kind: 'batch', batch_id: 'govramp-moderate@V1.06' }, after: [] },
            ...NIST_800_53_FAMILY_ORDER.map((f) => ({
                key: `govramp-moderate:${f}`, label: `Baseline members · ${f}`, migration: '326',
                match: { kind: 'batch', batch_id: `govramp-moderate@V1.06:${f}` },
                after: ['govramp-moderate', nistKey(f)],
            })),
            ...LOW_HIGH_BASELINES.flatMap((b) => [
                { key: b.key, label: `${b.label} baseline`, migration: b.migration, match: { kind: 'batch', batch_id: `${b.key}@${b.version}` }, after: [] },
                ...NIST_800_53_FAMILY_ORDER.map((f) => ({
                    key: `${b.key}:${f}`, label: `${b.label} members · ${f}`, migration: b.migration,
                    match: { kind: 'batch', batch_id: `${b.key}@${b.version}:${f}` },
                    after: [b.key, nistKey(f), ...(b.key === 'govramp-high' && HIGH_SET_FAMILIES.includes(f) ? [nistHighKey(f)] : [])],
                })),
            ]),
        ],
    },
    {
        stage: 4, key: 'cmmc', label: 'CMMC content',
        entries: [
            { key: 'cmmc-l2-catalog', label: 'CMMC L2 requirements and objectives', migration: '299', match: { kind: 'batch', batch_id: 'cmmc-l2-catalog@sp800-171a-2018' }, after: [] },
            { key: 'cmmc-l1-far', label: 'CMMC L1 FAR requirements', migration: '307', match: { kind: 'batch', batch_id: 'cmmc-l1-far@52.204-21-nov2021' }, after: ['cmmc-l2-catalog'] },
            { key: 'cmmc-test-library', label: 'Assessment test library', migration: '272, 300', match: { kind: 'batch', batch_id: 'cmmc-test-library@v102-1' }, after: ['cmmc-l2-catalog'] },
            { key: 'cmmc-crosswalk', label: 'CMMC crosswalk edges', migration: '274', match: { kind: 'batch', batch_id: 'cmmc-crosswalk@v102-1' }, after: ['cmmc-l2-catalog'] },
        ],
    },
    {
        stage: 5, key: 'test-maps', label: 'Objectives and test maps',
        entries: [
            { key: 'ksi-objectives', label: 'KSI objectives', migration: '337', match: { kind: 'batch', batch_id: 'ksi-objectives@1' }, after: ['fedramp-rules'] },
            ...CORE_READY_FAMILIES.map((f) => ({
                key: tm1Key(f), label: `GovRAMP Core/Ready test maps · ${f}`, migration: '331a',
                match: { kind: 'batch', batch_id: `govramp-test-maps@v103-1:${f}` },
                after: [nistKey(f), 'cmmc-test-library'],
            })),
            ...KSI_THEMES.map((t) => ({
                key: ksiTmKey(t), label: `KSI test maps · ${KSI_THEME_NAMES[t]}`, migration: '331b',
                match: { kind: 'batch', batch_id: `ksi-test-maps@v103-ksi-1:${t}` },
                after: ['ksi-objectives', ...ALL_TM1],
            })),
            ...NIST_800_53_FAMILY_ORDER.map((f) => ({
                key: tm2Key(f), label: `GovRAMP Moderate test maps · ${f}`, migration: '332',
                match: { kind: 'batch', batch_id: `govramp-test-maps@v103-2:${f}` },
                after: [nistKey(f), ...ALL_TM1, ...ALL_KSI_TM],
            })),
            ...['CM', 'IA'].map((f) => ({
                key: `govramp-test-maps-3:${f}`, label: `Edge and MFA test maps · ${f}`, migration: '350',
                match: { kind: 'batch', batch_id: `govramp-test-maps@v103-3:${f}` },
                after: [...ALL_TM1, ...ALL_KSI_TM, ...ALL_TM2],
            })),
            // v10.9 V109-3-4: the slice's two new check keys aliased on tests that already map their objectives (676).
            // 676's logging.audit-events.coverage version builds on v10.6's SOC 2 CC7 version (506), the tip of that
            // test's chain, so the batch waits on it (one linear chain per test, check-test-version-chains.mjs).
            { key: 'slice-rule-aliases', label: 'Slice rule check aliases', migration: '676', match: { kind: 'batch', batch_id: 'slice-rule-aliases@v109-3' }, after: [...ALL_TM1, ...ALL_KSI_TM, ...ALL_TM2, soc2MapKey('CC7')] },
            // v10.9 V109-7-4 (LM-16): AC-18(3), SC-39, SI-16 not applicable for GCP resources on the benchmark test (679).
            { key: 'slice-map-gaps', label: 'Not applicable for Google Cloud (AC-18(3), SC-39, SI-16)', migration: '679', match: { kind: 'batch', batch_id: 'govramp-slice-map-gaps@v109-7' }, after: [...ALL_TM1, ...ALL_KSI_TM, ...ALL_TM2, ...ALL_HIGH_TM, soc2MapKey('CC7')] },
            // v10.9 LM-25: the Data Access audit-log test takes R07's alias from logging.audit-events.coverage (678).
            { key: 'slice-data-access-release', label: 'Audit events coverage hands over the Data Access check', migration: '678', match: { kind: 'batch', batch_id: 'govramp-slice-data-access@v109-7:release' }, after: ['slice-rule-aliases', 'slice-map-gaps', ...ALL_TM1, ...ALL_KSI_TM, ...ALL_TM2] },
            { key: 'slice-data-access', label: 'Data Access audit logs test (AU-2, AU-12)', migration: '678', match: { kind: 'batch', batch_id: 'govramp-slice-data-access@v109-7' }, after: ['slice-data-access-release'] },
            // v10.9 LM-26: R20's check key aliased to the just-in-time test (KSI-IAM-JIT.1 primary, 331b) (685).
            { key: 'slice-jit-alias', label: 'Just-in-time check alias (KSI-IAM-JIT)', migration: '685', match: { kind: 'batch', batch_id: 'ksi-slice-jit@v109-7' }, after: [...ALL_KSI_TM] },
            ...HIGH_MAP_FAMILIES.map((f) => ({
                key: `high-set-test-maps:${f}`, label: `High-set test maps · ${f}`, migration: '514',
                match: { kind: 'batch', batch_id: `800-53-test-maps@v106-1:${f}` },
                after: [nistHighKey(f), ...ALL_TM1, ...ALL_KSI_TM, ...ALL_TM2, ...ALL_TM3],
            })),
            ...APP_D_DIRECTIONS.flatMap((d) => APP_D_FAMILIES.map((f) => ({
                key: `${d.key}:${f}`, label: `${d.label} · ${f}`, migration: d.migration,
                match: { kind: 'batch', batch_id: `${d.batch}@v106-1:${f}` },
                after: ['cmmc-l2-catalog', 'cmmc-test-library', ...ALL_NIST, ...ALL_TM1, ...ALL_KSI_TM, ...ALL_TM2, ...ALL_TM3, ...ALL_HIGH_TM],
            }))),
        ],
    },
    {
        stage: 6, key: 'soc2', label: 'SOC 2 on points of focus',
        entries: [
            ...SOC2_SERIES.map((x) => ({
                key: soc2PofKey(x), label: `SOC 2 points of focus · ${x}`, migration: x.startsWith('CC') ? '503' : '504',
                match: { kind: 'batch', batch_id: `soc2-pof@2022:${x}` }, after: ['soc2-tsc'],
            })),
            ...SOC2_SERIES.map((x) => ({
                key: soc2MapKey(x), label: `SOC 2 test maps · ${x}`, migration: '506',
                match: { kind: 'batch', batch_id: `soc2-maps@v106:${x}` },
                after: [soc2PofKey(x), 'cmmc-test-library', ...ALL_TM1, ...ALL_KSI_TM, ...ALL_TM2, ...ALL_TM3, ...ALL_HIGH_TM],
            })),
            {
                key: 'soc2-readiness', label: 'SOC 2 readiness engine switch (owner gate; production only after V106-1-8)', migration: '508',
                match: { kind: 'batch', batch_id: 'soc2-readiness@v1' }, after: [...SOC2_SERIES.map(soc2PofKey), ...SOC2_SERIES.map(soc2MapKey)],
            },
        ],
    },
    {
        stage: 7, key: 'iso-42001', label: 'ISO 42001',
        entries: [
            ...ISO_PARTS.map((p) => ({ key: p.key, label: p.label, migration: p.migration, match: { kind: 'batch', batch_id: p.batch }, after: [] })),
            {
                key: 'iso-42001-test-maps', label: 'ISO 42001 test maps', migration: '532',
                match: { kind: 'batch', batch_id: 'iso-42001-test-maps@v106-5' }, after: [...ISO_PARTS.map((p) => p.key), 'cmmc-test-library'],
            },
            {
                key: 'iso-certification', label: 'ISO certification cycle rules (owner gate)', migration: '531b',
                match: { kind: 'batch', batch_id: 'iso-certification@v1' }, after: [...ISO_PARTS.map((p) => p.key), 'iso-42001-test-maps'],
            },
        ],
    },
    {
        stage: 8, key: 'ci-gate', label: 'CI gate evidence',
        entries: [
            {
                key: 'ci-gate-test', label: 'Signed CI governance gate test and maps', migration: '630',
                match: { kind: 'batch', batch_id: 'ci-gate-test@v108-1' },
                after: [nistKey('CM'), nistKey('SA'), nistHighKey('CM'), 'ksi-objectives', soc2PofKey('CC8')],
            },
        ],
    },
    {
        stage: 9, key: 'rule-sets', label: 'Program rule sets',
        entries: [
            { key: 'govramp-rules', label: 'GovRAMP program rules', migration: '328', match: { kind: 'change_prefix', prefix: 'chg-m328-' }, after: ['govramp-moderate', ...GOVRAMP_MEMBERS] },
            { key: 'fedramp-readiness', label: 'FedRAMP readiness engine switch', migration: '341', match: { kind: 'batch', batch_id: 'fedramp-readiness@v1' }, after: ['fedramp-rules', 'ksi-objectives', ...ALL_KSI_TM] },
        ],
    },
    {
        stage: 10, key: 'catalogs', label: 'Provider catalog and clocks',
        entries: [
            { key: 'provider-inheritance', label: 'Provider inheritance catalog', migration: '343', match: { kind: 'batch_prefix', prefix: 'provider-inheritance@' }, after: ALL_NIST },
            { key: 'class-a-clocks', label: 'Class A duration clocks', migration: '339', match: { kind: 'change_prefix', prefix: 'chg-m339-' }, after: ['fedramp-rules'] },
        ],
    },
    {
        stage: 11, key: 'held', label: 'Held',
        entries: [
            {
                key: 'ksi-edge-downgrade', label: 'SEC → KSI edges become references', migration: '346',
                match: { kind: 'batch', batch_id: 'ksi-edge-downgrade@v103' }, after: ['fedramp-readiness'],
                hold: {
                    anchor_change_id: FEDRAMP_READINESS_CHANGE_ID, days: 30, environment: 'production',
                    reason: 'Approve 30 days after the readiness switch (341) is approved on production',
                },
            },
            {
                key: 'soc2-edge-downgrade', label: 'SEC → SOC 2 edges become references', migration: '507',
                match: { kind: 'batch', batch_id: 'soc2-edge-downgrade@v106' }, after: ['soc2-readiness', 'crosswalk-v96'],
                hold: {
                    anchor_change_id: SOC2_READINESS_CHANGE_ID, days: 30, environment: 'production',
                    reason: 'Approve 30 days after the SOC 2 readiness switch (508) is approved on production',
                },
            },
        ],
    },
    {
        stage: 12, key: 'earlier', label: 'Earlier releases',
        entries: [
            { key: 'soc2-tsc', label: 'SOC 2 TSC catalog', migration: '204', match: { kind: 'batch', batch_id: 'soc2-tsc@2017-rev2022' }, after: [] },
            { key: 'crosswalk-v96', label: 'Security baseline crosswalk', migration: '205', match: { kind: 'batch', batch_id: 'crosswalk-v96@1' }, after: ['soc2-tsc', 'fedramp-rules'] },
            { key: 'gcp-mappings', label: 'GCP mapping library', migration: '221', match: { kind: 'batch', batch_id: 'gcp-mappings@1' }, after: [] },
        ],
    },
    {
        stage: 13, key: 'ai-insurability', label: 'AI insurability (v11.1)',
        entries: [
            // 734's edges point at 733's threat-id nodes: nodes first (edges defer until then).
            { key: 'ai-threat-ids', label: 'AI threat ids (OWASP LLM / Agentic, MITRE ATLAS)', migration: '733', match: { kind: 'batch', batch_id: 'ai-threat-ids-v111@1' }, after: [] },
            { key: 'crosswalk-v111', label: 'AI threat crosswalk', migration: '734', match: { kind: 'batch', batch_id: 'crosswalk-v111@1' }, after: ['ai-threat-ids'] },
        ],
    },
    {
        stage: 14, key: 'ai-monitoring', label: 'Insurable AI monitoring (v11.3)',
        entries: [
            // V113-6-3: the owner approves these in this order. Control specs (840, 831's spec) are approved one by one.
            { key: 'promptfoo-signals-v113', label: 'Promptfoo signal discriminators', migration: '840', match: { kind: 'batch', batch_id: 'promptfoo-signals-v113@1' }, after: [] },
            { key: 'monitoring-rules-v113', label: 'Monitoring rule sets (class, prescriptions, vocabulary, candidates)', migration: '804g, 805, 806, 807', match: { kind: 'batch', batch_id: 'monitoring-rules-v113@1' }, after: [] },
            {
                key: 'anomaly-rules-v113', label: 'Anomaly and drift rules', migration: '810–814', match: { kind: 'batch', batch_id: 'anomaly-rules-v113@1' }, after: ['monitoring-rules-v113'],
                when_missing: { hint: 'Filed by v11.3 Track 2 (migrations 810–814) when it lands.' },
            },
            // V113-5-1 (ADR-0103 §2): the pack material-changes list; until approved, pack validity reads current with "rules pending".
            { key: 'pack-validity-v113', label: 'Pack material-changes list (what invalidates or stales a signed insurability pack)', migration: '826', match: { kind: 'batch', batch_id: 'pack-validity-v113@1' }, after: ['monitoring-rules-v113'] },
            { key: 'monitoring-obligations-v113', label: 'Monitoring obligations (EU AI Act Art. 72, ISO 42001 9.1 / A.6.2.6)', migration: '830, 831, 832', match: { kind: 'batch', batch_id: 'monitoring-obligations-v113@1' }, after: ['iso-42001:clauses', 'iso-42001:annex-a'] },
            { key: 'monitoring-crosswalk-v113', label: 'Monitoring crosswalk (AI RMF MANAGE 4.1, 800-53 CA-7 / SI-4, SOC 2 CC7)', migration: '833, 834', match: { kind: 'batch', batch_id: 'monitoring-crosswalk-v113@1' }, after: ['soc2-tsc', nistKey('CA'), nistKey('SI')] },
        ],
    },
    {
        stage: 15, key: 'gap-fulfillment', label: 'Gap fulfillment (v11.4)',
        entries: [
            // V114-6-3: briefs speak to the coverage reasons the v11.3 prescriptions produce, so they follow
            // that stage; the SLA / retry knobs follow the briefs (one Track 6 approval order). Until each row
            // is approved the generic family briefs and the hard-coded fallbacks stand — no behavior change.
            { key: 'fulfillment-briefs-v114', label: 'Fulfillment brief templates (one per gap class)', migration: '880', match: { kind: 'batch', batch_id: 'fulfillment-briefs-v114@1' }, after: ['monitoring-rules-v113'] },
            { key: 'gap-fulfillment-rules-v114', label: 'Gap finding SLA defaults and the webhook retry policy', migration: '883, 884', match: { kind: 'batch', batch_id: 'gap-fulfillment-rules-v114@1' }, after: ['fulfillment-briefs-v114'] },
        ],
    },
    {
        stage: 16, key: 'legal-sources', label: 'Legal source record (v11.7)',
        entries: [
            // V117-0-3: the record fixes first (1010's IL rule link waits for 1001's leg-il-hb2557 create).
            { key: 'legal-records-fix-v117', label: 'Legal record fixes (2026-10-26 verification)', migration: '1001', match: { kind: 'batch', batch_id: 'legal-records-fix-v117@1' }, after: [] },
            // V117-1-7 / V117-1-8 (ADR-0111): the jurisdiction source registry, then sessions, CO SB 26-189's stay and the rule → law links.
            { key: 'legal-registry-v117', label: 'Jurisdiction source registry', migration: '1009', match: { kind: 'batch', batch_id: 'legal-registry-v117@1' }, after: [] },
            { key: 'legal-sessions-links-v117', label: 'Legislative sessions, source documents, rule → law links', migration: '1010a', match: { kind: 'batch', batch_id: 'legal-sessions-links-v117@1' }, after: ['legal-records-fix-v117'] },
            // Review #463 H1: approving the CO stay drops its rules' obligations into "Decision needed". It was held until a
            // workspace could decide in the product; V117-1-10 (the Decision-needed view) shipped and released the hold.
            {
                key: 'legal-co-stay-v117', label: 'Colorado SB 26-189: enforcement paused (contested law)', migration: '1010b',
                match: { kind: 'batch', batch_id: 'legal-co-stay-v117@1' }, after: ['legal-sessions-links-v117'],
            },
            // V117-4-3 / 4-4: the state AI-law control batches.
            ...STATE_LAW_CONTROLS.map(([id, m]) => stateLawEntry(id, m)),
        ],
    },
    {
        stage: 17, key: 'traceable-controls', label: 'Traceable controls (v11.6)',
        entries: [
            // V116-1-2 (ADR-0115): each framework's publication link, shown on a control's sources.
            { key: 'framework-publications-v116', label: 'Framework publication links', migration: '950', match: { kind: 'batch', batch_id: 'framework-publications-v116@1' }, after: [] },
            // V116-2: the case links name 956's controls, so those come first.
            { key: 'case-controls-v116', label: 'Controls court cases link to', migration: '956', match: { kind: 'batch', batch_id: 'case-controls-v116@1' }, after: [] },
            { key: 'court-cases-v116', label: 'Court cases and their control links', migration: '957', match: { kind: 'batch', batch_id: 'court-cases-v116@1' }, after: ['case-controls-v116'] },
            // V116-4-6: 980 was filed without a batch id, so it is matched by change-id prefix.
            { key: 'rr026-tracking-v116', label: 'RR-026 narrowed to tracking', migration: '980', match: { kind: 'change_prefix', prefix: 'chg-m980-' }, after: [] },
            // V116-3: duty controls (then their specs, one by one), the AI Act record, then the rules one by one.
            { key: 'eu-ai-act-controls-v116', label: 'EU AI Act duty controls and specs', migration: '966', match: { kind: 'batch', batch_id: 'eu-ai-act-controls-v116@1' }, after: [] },
            { key: 'eu-ai-act-sources-v116', label: 'EU AI Act and Digital Omnibus: law record and source documents', migration: '963', match: { kind: 'batch', batch_id: 'eu-ai-act-sources-v116@1' }, after: ['eu-ai-act-controls-v116'] },
            { key: 'eu-ai-act-dates-v116', label: 'EU AI Act rules re-dated by the Omnibus (Annex III, Art. 50)', migration: '964, 965', match: { kind: 'batch', batch_id: 'eu-ai-act-dates-v116@1' }, after: ['eu-ai-act-sources-v116'] },
            { key: 'eu-ai-act-rules-v116', label: 'New EU AI Act rules (Arts 4, 5, 27, 50(4), 53; Annex III 3 and 6)', migration: '967, 968, 969', match: { kind: 'batch', batch_id: 'eu-ai-act-rules-v116@1' }, after: ['eu-ai-act-sources-v116'] },
            // V116-5: each pack control's spec 1.1.0 (approved on its own), then its guide where v11.6 writes one.
            ...PACK_GUIDED.map(([id, m]) => packEntry(id, m, true)),
            ...PACK_CHECKS.map((id) => packEntry(id, '995', false)),
        ],
    },
];
/** Entries whose rows carry no batch id: grouped by change-id prefix. */
export function contentBatchRowGroups(order = CONTENT_BATCH_ORDER) {
    return order.flatMap((s) => s.entries)
        .filter((e) => e.match.kind === 'change_prefix')
        .map((e) => ({ group_key: rowGroupKey(e.key), prefix: e.match.prefix }));
}
/** Group key for an entry matched by change-id prefix (`rows:<entry key>`). */
export function rowGroupKey(entryKey) {
    return `rows:${entryKey}`;
}
/** Change-id prefix behind a `rows:<entry key>` group, or null. */
export function changePrefixForGroup(groupKey, order = CONTENT_BATCH_ORDER) {
    if (!groupKey.startsWith('rows:'))
        return null;
    const entry = order.flatMap((s) => s.entries).find((e) => rowGroupKey(e.key) === groupKey);
    return entry && entry.match.kind === 'change_prefix' ? entry.match.prefix : null;
}
export function entryMatches(entry, s) {
    const m = entry.match;
    if (m.kind === 'change_prefix')
        return s.batch_id === null && s.group_key === rowGroupKey(entry.key);
    if (s.batch_id === null)
        return false;
    return m.kind === 'batch' ? s.batch_id === m.batch_id : s.batch_id.startsWith(m.prefix);
}
/** The order entry for a batch id (or row group), if the order knows it. */
export function findContentBatchEntry(groupKey, order = CONTENT_BATCH_ORDER) {
    const probe = { group_key: groupKey, batch_id: groupKey.startsWith('rows:') ? null : groupKey };
    for (const stage of order) {
        for (const entry of stage.entries)
            if (entryMatches(entry, probe))
                return { stage, entry };
    }
    return null;
}
/**
 * Hold state for an entry: null when released, else the reason (and the end of
 * a running clock). Released only on the hold's environment, once the anchor is
 * approved and `days` have passed since its review.
 */
export function evaluateContentBatchHold(hold, ctx) {
    if (isShipHold(hold))
        return { reason: hold.reason, until: null };
    const anchor = ctx.anchors?.[hold.anchor_change_id];
    const decided = anchor && (anchor.status === 'approved' || anchor.status === 'applied') && anchor.reviewed_at;
    if (ctx.environment !== hold.environment || !decided)
        return { reason: hold.reason, until: null };
    const until = new Date(Date.parse(anchor.reviewed_at) + hold.days * 86_400_000);
    if (Number.isNaN(until.getTime()))
        return { reason: hold.reason, until: null };
    if ((ctx.now ?? new Date()).getTime() >= until.getTime())
        return null;
    return { reason: `${hold.reason} · clock ends ${until.toISOString().slice(0, 10)}`, until: until.toISOString() };
}
/**
 * Order the pending batches: known entries by stage and position, unknown
 * batches last ("Other", oldest first). Batches with nothing pending are left out.
 */
export function deriveContentBatchQueue(summaries, ctx = {}) {
    const order = ctx.order ?? CONTENT_BATCH_ORDER;
    const live = summaries.filter((s) => s.pending > 0);
    const entries = order.flatMap((stage) => stage.entries.map((entry) => ({ stage, entry })));
    const byEntry = new Map();
    const claimed = new Set();
    for (const { entry } of entries) {
        const mine = live.filter((s) => !claimed.has(s.group_key) && entryMatches(entry, s))
            .sort((a, b) => a.group_key.localeCompare(b.group_key));
        mine.forEach((s) => claimed.add(s.group_key));
        byEntry.set(entry.key, mine);
    }
    const labelOf = new Map(entries.map(({ entry }) => [entry.key, entry.label]));
    const entryOf = new Map(entries.map(({ entry }) => [entry.key, entry]));
    const appliedKeys = ctx.applied ? [...ctx.applied] : null;
    const everApplied = (k) => {
        const e = entryOf.get(k);
        return !appliedKeys || !e || appliedKeys.some((g) => entryMatches(e, { group_key: g, batch_id: g.startsWith('rows:') ? null : g }));
    };
    const rows = [];
    for (const { stage, entry } of entries) {
        for (const s of byEntry.get(entry.key) ?? []) {
            const waiting_on = entry.after.flatMap((k) => {
                const open = byEntry.get(k) ?? [];
                if (open.length)
                    return open.map((p) => ({ group_key: p.group_key, label: labelOf.get(k) ?? k }));
                if (everApplied(k))
                    return [];
                const miss = entryOf.get(k)?.when_missing;
                return [{ group_key: `missing:${k}`, label: labelOf.get(k) ?? k, missing: true, hint: miss?.hint ?? null, action: miss?.action ?? null }];
            });
            const hold = entry.hold ? evaluateContentBatchHold(entry.hold, ctx) : null;
            const state = hold ? 'held'
                : waiting_on.length ? 'waiting'
                    : s.batch_id === null || s.batchable === 0 ? 'individual'
                        : 'ready';
            rows.push({
                ...s, stage: stage.stage, stage_label: stage.label, entry_key: entry.key, label: entry.label,
                migration: entry.migration, state, waiting_on, hold_reason: hold?.reason ?? null,
                hold_until: hold?.until ?? null, approvable: state === 'ready', next: false,
            });
        }
    }
    const other = live.filter((s) => !claimed.has(s.group_key))
        .sort((a, b) => (a.oldest_created_at ?? '').localeCompare(b.oldest_created_at ?? '') || a.group_key.localeCompare(b.group_key));
    for (const s of other) {
        const state = s.batch_id === null || s.batchable === 0 ? 'individual' : 'ready';
        rows.push({
            ...s, stage: null, stage_label: 'Other', entry_key: null, label: s.group_key, migration: null,
            state, waiting_on: [], hold_reason: null, hold_until: null, approvable: state === 'ready', next: false,
        });
    }
    const first = rows.find((r) => r.approvable);
    if (first)
        first.next = true;
    return rows;
}
/**
 * Rows of a batch that "Approve all" does not apply: open, not deferred, and not a batch target type
 * (e.g. a control spec filed with the batch id). The Batches view says they need separate approval.
 */
export function separateApprovalRows(r) {
    return Math.max(0, r.pending - r.batchable - r.deferred);
}
/** Human label for those rows, or null when there are none. */
export function separateApprovalLabel(r) {
    const n = separateApprovalRows(r);
    return n === 0 ? null : `${n} row${n === 1 ? '' : 's'} need${n === 1 ? 's' : ''} separate approval`;
}
/**
 * V112-5-4 / ADR-0116 (owner D-BL55-4): what "Approve all" adds beyond catalog rows — the new vocabulary terms and the
 * rules the run approves with them. Null when the batch carries neither.
 */
export function batchVocabularyLabel(r) {
    const terms = r.new_terms ?? [];
    if (!terms.length)
        return null;
    const shown = terms.slice(0, 3).map((t) => `“${t}”`).join(', ');
    const more = terms.length > 3 ? ` and ${terms.length - 3} more` : '';
    const t = `Adds ${terms.length === 1 ? 'a new term' : `${terms.length} new terms`} to the vocabulary: ${shown}${more}.`;
    const rr = r.rules ? ` Also approves the ${r.rules === 1 ? 'rule that uses' : `${r.rules} rules that use`} ${terms.length === 1 ? 'it' : 'them'}.` : '';
    return t + rr;
}
/** Every change id a hold in the order is anchored on. */
export function contentBatchHoldAnchors(order = CONTENT_BATCH_ORDER) {
    return [...new Set(order.flatMap((s) => s.entries).flatMap((e) => (e.hold && !isShipHold(e.hold) ? [e.hold.anchor_change_id] : [])))];
}
//# sourceMappingURL=content-batch-order.js.map